import { useFragment } from "@/generated";
import { AudioTrackFieldsFragmentDoc, GetAudioTracksByFolderIdDocument } from "@/generated/graphql";
import { useQuery } from "@apollo/client/react";

/** Tracks are a plain list with no thumbnails, so a larger page than cards is fine. */
const TRACK_PAGE_SIZE = 50;

/**
 * Fetches the audio tracks inside an audio folder, in display order.
 *
 * The server already sorts by sortOrder, but the list is re-sorted here for the same
 * reason project lists are: reordering writes new sortOrders straight into the cache,
 * and without a client-side sort the row would stay where it was until a refetch.
 */
export function useAudioTracks(folderId: string) {
  const { data, loading, error, refetch, fetchMore } = useQuery(GetAudioTracksByFolderIdDocument, {
    variables: { folderId, limit: TRACK_PAGE_SIZE, offset: 0 },
    fetchPolicy: "cache-and-network",
  });

  const maskedItems = data?.audioTracksByFolderId.items ?? [];
  const tracks = maskedItems
    .map((track) => useFragment(AudioTrackFieldsFragmentDoc, track))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const hasMore = data?.audioTracksByFolderId.hasMore ?? false;

  const loadMore = () => {
    fetchMore({
      variables: { folderId, limit: TRACK_PAGE_SIZE, offset: maskedItems.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const existingIds = new Set(
          useFragment(AudioTrackFieldsFragmentDoc, prev.audioTracksByFolderId.items).map(
            (track) => track.id
          )
        );
        const newItems = fetchMoreResult.audioTracksByFolderId.items.filter(
          (item) => !existingIds.has(useFragment(AudioTrackFieldsFragmentDoc, item).id)
        );

        return {
          audioTracksByFolderId: {
            ...fetchMoreResult.audioTracksByFolderId,
            items: [...prev.audioTracksByFolderId.items, ...newItems],
          },
        };
      },
    });
  };

  return { tracks, hasMore, loading, error, refetch, loadMore };
}
